import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { ARIA_CARD_ACCENT, ARIA_CARD_ACCENT_SOFT } from '../ariaMessageTypes';

const AudienceButton = ({ audience, selected, disabled }) => (
  <Button
    fullWidth
    disabled={disabled}
    variant="outlined"
    sx={{
      textTransform: 'none',
      borderRadius: '12px',
      py: 1.1,
      px: 1,
      minHeight: 0,
      flex: 1,
      alignItems: 'flex-start',
      flexDirection: 'column',
      borderColor: selected ? ARIA_CARD_ACCENT : 'divider',
      bgcolor: selected ? 'primary.light' : 'background.paper',
      color: selected ? ARIA_CARD_ACCENT : 'text.secondary',
      '&.Mui-disabled': {
        borderColor: selected ? ARIA_CARD_ACCENT : 'divider',
        bgcolor: selected ? 'primary.light' : 'background.paper',
        color: selected ? ARIA_CARD_ACCENT : 'text.disabled',
        opacity: 1,
      },
    }}
  >
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.3 }}>
      {audience.title}
    </Typography>
    {audience.subtitle ? (
      <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, lineHeight: 1.35, mt: 0.25 }}>
        {audience.subtitle}
      </Typography>
    ) : null}
  </Button>
);

/**
 * Post-event share prompt card (message.data.ariaMessageType = post_event_share_prompt).
 * Admin Handsoff: display-only — reflects posted / audience state without actions.
 */
const AriaPostEventSharePromptCard = ({ prompt }) => {
  if (!prompt?.eventTitle && !prompt?.eventId) return null;

  const sharedPost = prompt.sharedPost ?? {};
  const audiences = prompt.audiences ?? [];
  const selectedAudience = sharedPost.audience;
  const imageUrl = sharedPost.imageUrl;
  const caption = sharedPost.caption;
  const eventDateLine = prompt.eventDateLine;
  const pillLabel = [prompt.eventTitle, eventDateLine].filter(Boolean).join(' · ');

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 340 },
        maxWidth: '100%',
        minWidth: { sm: 300 },
        borderRadius: '16px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(17, 24, 39, 0.06)',
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5 }}>
        {pillLabel ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1.25,
              px: 1,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: ARIA_CARD_ACCENT_SOFT,
              border: `1px solid ${ARIA_CARD_ACCENT}33`,
              maxWidth: '100%',
            }}
          >
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 14, color: ARIA_CARD_ACCENT, flexShrink: 0 }}
            />
            <Typography
              noWrap
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: ARIA_CARD_ACCENT,
                lineHeight: 1.3,
              }}
            >
              {pillLabel}
            </Typography>
          </Box>
        ) : null}

        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{
              width: '100%',
              borderRadius: '12px',
              display: 'block',
              objectFit: 'cover',
              maxHeight: 200,
              mb: 1.25,
              bgcolor: 'action.hover',
            }}
          />
        ) : (
          <Box
            sx={{
              mb: 1.25,
              py: 3,
              px: 1.5,
              borderRadius: '12px',
              border: '1px dashed',
              borderColor: 'text.disabled',
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            <PhotoCameraOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 500 }}>
              Add a photo
            </Typography>
          </Box>
        )}

        {caption ? (
          <Box
            sx={{
              mb: 1.25,
              borderRadius: '12px',
              bgcolor: (theme) => theme.portal?.accentSoft ?? theme.palette.action.hover,
              border: '1px solid',
              borderColor: (theme) => theme.portal?.accentMuted ?? theme.palette.divider,
              px: 1.25,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.5625rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: 'primary.main',
                mb: 0.75,
              }}
            >
              ✨ ARIA DRAFTED THIS FROM YOUR RATING — EDIT ANYTIME
            </Typography>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '10px',
                px: 1.25,
                py: 1,
                borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  fontStyle: 'italic',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}
              >
                “{caption}”
              </Typography>
            </Box>
          </Box>
        ) : null}

        {audiences.length > 0 ? (
          <Box sx={{ mb: 1.25 }}>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 0.75,
              }}
            >
              Share with
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {audiences.map((audience) => (
                <AudienceButton
                  key={audience.key}
                  audience={audience}
                  selected={audience.key === selectedAudience}
                  disabled
                />
              ))}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            fullWidth
            variant="contained"
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '12px',
              py: 1,
              bgcolor: ARIA_CARD_ACCENT,
              boxShadow: 'none',
              '&.Mui-disabled': {
                bgcolor: prompt.posted ? 'action.disabledBackground' : ARIA_CARD_ACCENT,
                color: prompt.posted ? 'text.secondary' : 'common.white',
                opacity: prompt.posted ? 1 : 0.55,
              },
            }}
          >
            {prompt.posted ? 'Posted' : 'Post to feed'}
          </Button>
          <Button
            variant="outlined"
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderRadius: '12px',
              px: 2,
              minWidth: 0,
              color: 'text.secondary',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              whiteSpace: 'nowrap',
              '&.Mui-disabled': {
                color: 'text.disabled',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                opacity: 1,
              },
            }}
          >
            Not now
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AriaPostEventSharePromptCard;
